/**
 * FmScatterChart — measured x/y points (e.g. diameter × height) per group.
 *
 * Both axes are numeric: a measured quantity spaced as categories would list
 * values in arrival order ("14 18 22 12 19") instead of by value.
 */
import React from 'react';
import { CartesianGrid, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import type { FmChartSize, FmScatterChartSpec } from './chartTypes';
import {
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { FmChartFrame } from './FmChartFrame';
import { resolveSeriesColor } from './seriesColor';

export type FmScatterChartProps = Omit<FmScatterChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const POINT_OPACITY = 0.75;

export const FmScatterChart: React.FC<FmScatterChartProps> = ({
  groups,
  xLabel,
  yLabel,
  size = 'md',
  'data-testid': testId,
}) => (
  <FmChartFrame size={size} data-testid={testId} data-kind="scatter">
    <ScatterChart margin={chartMargin(yLabel)} accessibilityLayer>
      <CartesianGrid {...CHART_GRID_PROPS} />
      <XAxis
        type="number"
        dataKey="x"
        name={xLabel}
        domain={['auto', 'auto']}
        {...CHART_XAXIS_PROPS}
        label={xAxisTitle(xLabel)}
      />
      <YAxis
        type="number"
        dataKey="y"
        name={yLabel}
        domain={['auto', 'auto']}
        {...CHART_YAXIS_PROPS}
        label={yAxisTitle(yLabel)}
      />
      <Tooltip content={<FmChartTooltip />} cursor={{ strokeDasharray: '3 3' }} />
      {groups.map((group) => (
        <Scatter
          key={group.id}
          name={group.label}
          data={group.points.map((point) => ({ ...point }))}
          fill={resolveSeriesColor(group.color)}
          fillOpacity={POINT_OPACITY}
          isAnimationActive={false}
        />
      ))}
    </ScatterChart>
  </FmChartFrame>
);
